import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import WhatsAppButton from "@/components/WhatsAppButton";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { PostRequirementDialog } from "@/components/PostRequirementDialog";
import { FaqSection } from "@/components/FaqSection";
import { useSeo } from "@/lib/seo";
import { AnimatedHeadline } from "@/components/AnimatedHeadline";
import { LiveCounter } from "@/components/LiveCounter";
import { PathsSection } from "@/components/funnel/PathCards";
import { LogoWall } from "@/components/funnel/LogoWall";

const BOLD = { b: <b /> };
import {
  GraduationCap, BookOpen, Award, Briefcase, ArrowRight, CheckCircle2,
  PlayCircle, Building2, Sparkles, UserCheck, Users, Target, TrendingUp, Mail, MapPin,
  Presentation, IndianRupee, Loader2,
} from "lucide-react";

const modelIcons = [Building2, UserCheck, Award, Briefcase];
const missionIcons = [TrendingUp, Building2, UserCheck, Briefcase];
const audienceIcons = [Users, Target];
const audienceImgs = [
  "https://images.unsplash.com/photo-1758270705482-cee87ea98738?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1758518730083-4c12527b6742?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
];
const galleryImgs = [
  "https://images.unsplash.com/photo-1591123120675-6f7f1aae0e5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1663162550938-60f70fab5d31?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1577415124269-fc1140a69e91?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];
const partners = ["Nova Labs", "InsightIQ", "BrightReach", "CloudStack"];

function TeachWithUs() {
  const { t } = useTranslation();
  const empty = { name: "", email: "", expertise: "", experience_years: "", hourly_rate: "", linkedin: "", bio: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const points = t("landing.teachPoints", { returnObjects: true });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/experts/apply", {
        name: f.name,
        email: f.email,
        expertise: f.expertise,
        experience_years: parseInt(f.experience_years || "0", 10) || 0,
        hourly_rate: parseFloat(f.hourly_rate || "0") || 0,
        linkedin: f.linkedin,
        bio: f.bio,
      });
      toast.success(t("landing.teachForm.success"));
      setF(empty);
    } catch (err) {
      toast.error(t("landing.teachForm.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="teach" className="border-y border-slate-100 bg-slate-50">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 py-20 lg:grid-cols-[1fr_1.1fr] lg:items-start">
        <div className="lg:sticky lg:top-24">
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
            <Presentation className="h-3.5 w-3.5" /> {t("landing.teachBadge")}
          </span>
          <h2 className="mt-4 font-heading text-3xl font-extrabold sm:text-4xl">{t("landing.teachTitle")}</h2>
          <p className="mt-4 max-w-md text-lg text-slate-600 leading-relaxed" data-testid="teach-subtitle"><Trans i18nKey="landing.teachSubtitle" components={BOLD} /></p>
          <ul className="mt-6 space-y-3">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="teach-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="ex-name">{t("landing.teachForm.fullName")}</Label>
              <Input id="ex-name" required value={f.name} onChange={set("name")} className="mt-1.5" data-testid="expert-name" />
            </div>
            <div>
              <Label htmlFor="ex-email">{t("landing.teachForm.email")}</Label>
              <Input id="ex-email" type="email" required value={f.email} onChange={set("email")} className="mt-1.5" data-testid="expert-email" />
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="ex-expertise">{t("landing.teachForm.expertise")}</Label>
            <Input id="ex-expertise" required placeholder={t("landing.teachForm.expertisePlaceholder")} value={f.expertise} onChange={set("expertise")} className="mt-1.5" data-testid="expert-expertise" />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="ex-exp">{t("landing.teachForm.years")}</Label>
              <Input id="ex-exp" type="number" min="0" value={f.experience_years} onChange={set("experience_years")} className="mt-1.5" data-testid="expert-experience" />
            </div>
            <div>
              <Label htmlFor="ex-rate">{t("landing.teachForm.rate")}</Label>
              <div className="relative mt-1.5">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="ex-rate" type="number" min="0" required placeholder={t("landing.teachForm.ratePlaceholder")} value={f.hourly_rate} onChange={set("hourly_rate")} className="pl-9" data-testid="expert-rate" />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="ex-linkedin">{t("landing.teachForm.linkedin")}</Label>
            <Input id="ex-linkedin" placeholder="https://…" value={f.linkedin} onChange={set("linkedin")} className="mt-1.5" data-testid="expert-linkedin" />
          </div>
          <div className="mt-4">
            <Label htmlFor="ex-bio">{t("landing.teachForm.bio")}</Label>
            <Textarea id="ex-bio" rows={3} placeholder={t("landing.teachForm.bioPlaceholder")} value={f.bio} onChange={set("bio")} className="mt-1.5" data-testid="expert-bio" />
          </div>
          <Button type="submit" disabled={busy} className="mt-6 w-full bg-amber-600 hover:bg-amber-700 h-11" data-testid="expert-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("landing.teachForm.submit")}
          </Button>
        </form>
      </div>
    </section>
  );
}

function FeaturedCourses() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/public/courses").then(({ data }) => {
      const featured = data.filter((c) => c.featured).sort((a, b) => (a.featured_order ?? 0) - (b.featured_order ?? 0));
      setCourses((featured.length > 0 ? featured : data).slice(0, 3));
    }).catch(() => {});
  }, []);

  if (courses.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16" data-testid="featured-courses">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-600">{t("featured.eyebrow")}</span>
          <h2 className="mt-2 font-heading text-3xl font-extrabold sm:text-4xl">{t("featured.title")}</h2>
        </div>
        <button onClick={() => navigate("/browse")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700" data-testid="featured-view-all">
          {t("featured.viewAll")} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {courses.map((c) => (
          <div
            key={c.course_id}
            data-testid="featured-course-card"
            onClick={() => navigate(`/browse/courses/${c.course_id}`)}
            className="group cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="h-40 overflow-hidden">
              <img src={c.thumbnail} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded-full bg-amber-50 px-2.5 py-0.5 font-semibold text-amber-700">{c.category}</span>
                <span className="text-slate-400">{c.level}</span>
              </div>
              <h3 className="mt-3 font-heading text-lg font-bold text-slate-900">{c.title}</h3>
              <p className="mt-1.5 text-sm text-slate-500 line-clamp-2">{c.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="flex items-center gap-1 text-xs text-slate-400"><BookOpen className="h-3.5 w-3.5" /> {c.lesson_count} {t("common.lessons")}</span>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-600 group-hover:gap-2 transition-all">
                  {t("publicCourse.viewSyllabus")} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function VisitorInterest() {
  const { t, i18n } = useTranslation();
  const empty = { name: "", email: "", phone: "", interest: "", message: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/visitors", { ...f, lang: (i18n.language || "en").slice(0, 2) });
      toast.success(t("visitor.success"));
      setF(empty);
      setDone(true);
    } catch (err) {
      toast.error(t("visitor.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="enquire" className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-start">
        <div className="lg:sticky lg:top-24">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <Mail className="h-3.5 w-3.5" /> {t("visitor.badge")}
          </span>
          <h2 className="mt-4 font-heading text-3xl font-extrabold sm:text-4xl">{t("visitor.title")}</h2>
          <p className="mt-4 max-w-md text-lg text-slate-600 leading-relaxed">{t("visitor.subtitle")}</p>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="visitor-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="v-name">{t("visitor.name")}</Label>
              <Input id="v-name" required value={f.name} onChange={set("name")} className="mt-1.5" data-testid="visitor-name" />
            </div>
            <div>
              <Label htmlFor="v-email">{t("visitor.email")}</Label>
              <Input id="v-email" type="email" required value={f.email} onChange={set("email")} className="mt-1.5" data-testid="visitor-email" />
            </div>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="v-phone">{t("visitor.phone")}</Label>
              <Input id="v-phone" value={f.phone} onChange={set("phone")} className="mt-1.5" data-testid="visitor-phone" />
            </div>
            <div>
              <Label htmlFor="v-interest">{t("visitor.interest")}</Label>
              <Input id="v-interest" placeholder={t("visitor.interestPlaceholder")} value={f.interest} onChange={set("interest")} className="mt-1.5" data-testid="visitor-interest" />
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="v-message">{t("visitor.message")}</Label>
            <Textarea id="v-message" rows={3} placeholder={t("visitor.messagePlaceholder")} value={f.message} onChange={set("message")} className="mt-1.5" data-testid="visitor-message" />
          </div>
          {done && (
            <p className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700" data-testid="visitor-thankyou">
              <CheckCircle2 className="h-4 w-4 shrink-0" /> {t("visitor.thankyou")}
            </p>
          )}
          <Button type="submit" disabled={busy} className="mt-6 w-full bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="visitor-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("visitor.submit")}
          </Button>
        </form>
      </div>
    </section>
  );
}

function EmployerShowcase() {
  const { t } = useTranslation();
  const [employers, setEmployers] = useState([]);

  useEffect(() => {
    api.get("/public/employers").then(({ data }) => setEmployers(data)).catch(() => {});
  }, []);

  return (
    <section id="employers" className="border-y border-amber-100 bg-gradient-to-b from-amber-50/60 to-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-950 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
              <Building2 className="h-3.5 w-3.5" /> {t("employers.badge")}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-extrabold sm:text-4xl lg:text-5xl tracking-tight">{t("employers.title")}</h2>
            <p className="mt-4 max-w-lg text-lg text-slate-600 leading-relaxed">{t("employers.subtitle")}</p>
            <div className="mt-8">
              <PostRequirementDialog
                mode="public"
                trigger={
                  <Button size="lg" className="h-12 bg-amber-600 hover:bg-amber-700 px-7 text-base font-bold" data-testid="post-requirement-landing">
                    {t("employers.postCta")} <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                }
              />
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              {t("employers.points", { returnObjects: true }).map((p) => (
                <li key={p} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> {p}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">{t("employers.trustedBy")}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {(employers.length ? employers : partners.map((n) => ({ company_name: n, industry: "" }))).slice(0, 9).map((e, i) => (
                <div key={`${e.company_name}-${i}`} className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm" data-testid="employer-card">
                  {e.logo_url ? (
                    <img src={e.logo_url} alt="" className="h-8 w-8 rounded-lg object-contain" />
                  ) : (
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-sm font-extrabold text-white">
                      {e.company_name?.[0]?.toUpperCase() || "C"}
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-slate-800">{e.company_name}</span>
                    {e.industry && <span className="block truncate text-xs text-slate-400">{e.industry}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const goToApp = () => navigate(user ? "/dashboard" : "/login");
  useSeo({
    title: "Classroom to Corporate — Job-Ready Courses & Placements | Ahmedabad",
    description: "Classroom to Corporate trains college students and fresh graduates in job-ready skills designed with industry, awards certificates, and connects them to hiring companies in Ahmedabad, Gujarat and across India.",
  });

  const heroTags = t("landing.heroTags", { returnObjects: true });
  const missionStats = t("landing.missionStats", { returnObjects: true });
  const model = t("landing.model", { returnObjects: true });
  const audiences = t("landing.audiences", { returnObjects: true });
  const gallery = t("landing.gallery", { returnObjects: true });
  const outcomes = t("landing.outcomes", { returnObjects: true });

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Classroom to Corporate" className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 object-contain" />
            <span className="font-heading text-lg sm:text-2xl font-extrabold leading-tight tracking-tight">
              <span className="text-slate-900">Classroom to </span>
              <span className="text-amber-600">Corporate</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/browse")} className="hidden sm:block text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="nav-browse">{t("nav.courses")}</button>
            <a href="#mission" className="hidden sm:block text-sm font-medium text-slate-600 hover:text-slate-900">{t("nav.mission")}</a>
            <a href="#how" className="hidden sm:block text-sm font-medium text-slate-600 hover:text-slate-900">{t("nav.howItWorks")}</a>
            <a href="#employers" className="hidden sm:block text-sm font-semibold text-amber-700 hover:text-amber-800" data-testid="nav-employers">{t("nav.forEmployers")}</a>
            <a href="#teach" className="hidden sm:block text-sm font-medium text-slate-600 hover:text-slate-900">{t("nav.teach")}</a>
            <a href="#enquire" className="hidden sm:block text-sm font-medium text-slate-600 hover:text-slate-900">{t("nav.enquire")}</a>
            <a href="#about" className="hidden sm:block text-sm font-medium text-slate-600 hover:text-slate-900">{t("nav.about")}</a>
            <LanguageSwitcher />
            <Button onClick={goToApp} className="bg-amber-600 hover:bg-amber-700" data-testid="nav-signin-button">
              {user ? t("common.goToDashboard") : t("common.signIn")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="hero-section">
        <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-amber-300/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 right-0 h-[28rem] w-[28rem] rounded-full bg-rose-300/30 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 py-16 lg:grid-cols-[1.15fr_1fr] lg:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-orange-500/30 sm:text-base" data-testid="hero-badge">
              <Sparkles className="h-4 w-4" /> {t("landing.heroBadge")}
            </span>
            <AnimatedHeadline line1={t("landing.heroTitle1")} line2={t("landing.heroTitle2")} />
            <p className="mt-7 max-w-2xl text-xl font-medium leading-relaxed text-slate-700 sm:text-2xl" data-testid="hero-subtitle">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button onClick={() => navigate("/browse")} size="lg" className="h-14 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-9 text-lg font-bold text-white shadow-xl shadow-orange-500/30 transition-transform hover:scale-[1.03] hover:from-amber-600 hover:to-rose-600" data-testid="hero-get-started">
                {t("landing.exploreCourses")} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="#how" className="inline-flex h-14 items-center gap-2 rounded-full border-2 border-orange-300 bg-white/70 px-7 text-lg font-bold text-orange-700 backdrop-blur transition-colors hover:bg-white" data-testid="hero-how-it-works">
                <PlayCircle className="h-6 w-6" /> {t("nav.howItWorks")}
              </a>
            </div>
            <LiveCounter />
            <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-tags">
              {heroTags.map((tag, i) => (
                <span key={tag} className={`flex items-center gap-2 rounded-full px-4 py-2 text-base font-bold shadow-sm ${["bg-emerald-100 text-emerald-800", "bg-sky-100 text-sky-800", "bg-rose-100 text-rose-800"][i % 3]}`}>
                  <CheckCircle2 className="h-5 w-5" /> {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-amber-100 to-transparent blur-2xl" />
            <img
              src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Graduates celebrating"
              className="relative w-full rounded-3xl object-cover shadow-2xl aspect-[4/3]"
            />
            <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-slate-100 bg-white p-4 shadow-xl sm:block">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-bold">{t("landing.badgePlacementTitle")}</p>
                  <p className="text-xs text-slate-400">{t("landing.badgePlacementSub")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LogoWall className="pt-14" titleKey="logoWall.homeTitle" />
      <PathsSection />

      {/* Mission / Problem */}
      <section id="mission" className="border-y border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
          <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-600">{t("landing.missionEyebrow")}</span>
              <h2 className="mt-2 font-heading text-3xl font-extrabold sm:text-4xl">
                {t("landing.missionTitle")}
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed" data-testid="mission-p1"><Trans i18nKey="landing.missionP1" components={BOLD} /></p>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                {t("landing.missionP2")}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {missionStats.map((s, i) => {
                const Icon = missionIcons[i];
                return (
                  <div key={s.l} className="rounded-2xl border border-slate-200 bg-white p-5">
                    <Icon className="h-6 w-6 text-amber-600" />
                    <p className="mt-3 font-heading text-2xl font-extrabold">{s.v}</p>
                    <p className="mt-1 text-sm text-slate-500">{s.l}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* How it works / Model */}
      <section id="how" className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-600">{t("landing.howEyebrow")}</span>
          <h2 className="mt-2 font-heading text-3xl font-extrabold sm:text-4xl">{t("landing.howTitle")}</h2>
          <p className="mt-3 text-slate-500">{t("landing.howSubtitle")}</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {model.map((m, i) => {
            const Icon = modelIcons[i];
            return (
              <div
                key={m.title}
                data-testid="feature-card"
                className="group relative rounded-2xl border border-slate-200 p-6 transition-all hover:-translate-y-1 hover:border-amber-200 hover:shadow-lg"
              >
                <span className="font-heading text-sm font-extrabold text-amber-200">0{i + 1}</span>
                <div className="mt-2 h-12 w-12 rounded-xl bg-amber-50 flex items-center justify-center transition-colors group-hover:bg-amber-600">
                  <Icon className="h-6 w-6 text-amber-600 transition-colors group-hover:text-white" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-bold">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{m.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Who we serve */}
      <section className="bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">{t("landing.serveEyebrow")}</span>
            <h2 className="mt-2 font-heading text-3xl font-extrabold sm:text-4xl">{t("landing.serveTitle")}</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {audiences.map((a, i) => {
              const Icon = audienceIcons[i];
              return (
                <div key={a.tag} className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                  <img src={audienceImgs[i]} alt="" className="h-44 w-full object-cover" />
                  <div className="p-8">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-xl bg-amber-600 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-amber-300">{a.tag}</span>
                    </div>
                    <h3 className="mt-5 font-heading text-2xl font-bold">{a.title}</h3>
                    <ul className="mt-5 space-y-3">
                      {a.points.map((p) => (
                        <li key={p} className="flex items-start gap-2.5 text-sm text-slate-300">
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400 mt-0.5" /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery: campus to corporate */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="max-w-2xl mb-8">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-600">{t("landing.galleryEyebrow")}</span>
          <h2 className="mt-2 font-heading text-3xl font-extrabold sm:text-4xl">{t("landing.galleryTitle")}</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {gallery.map((cap, i) => (
            <figure key={cap} className="group relative overflow-hidden rounded-2xl" data-testid="gallery-item">
              <img src={galleryImgs[i]} alt={cap} className="h-64 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-sm font-semibold text-white">
                {cap}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Employer showcase + hiring requirement */}
      <EmployerShowcase />

      {/* Outcomes */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="grid grid-cols-2 gap-6 rounded-3xl border border-slate-200 bg-slate-50 p-8 md:grid-cols-4">
          {outcomes.map((s) => (
            <div key={s.l} className="text-center" data-testid="outcome-stat">
              <p className="font-heading text-3xl font-extrabold text-amber-600">{s.v}</p>
              <p className="mt-1 text-sm text-slate-500">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About / Founder */}
      <section id="about" className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <img
            src="/founder-darshan.jpg?v=3"
            alt="Mr. Darshan Thakkar, Founder"
            className="w-full rounded-3xl object-cover object-top shadow-xl aspect-[4/5]"
          />
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-600">{t("landing.aboutEyebrow")}</span>
            <h2 className="mt-2 font-heading text-3xl font-extrabold sm:text-4xl">{t("landing.aboutTitle")}</h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              {t("landing.aboutP1")}
            </p>
            <blockquote className="mt-6 rounded-2xl border-l-4 border-amber-600 bg-amber-50 p-6" data-testid="founder-quote">
              <p className="font-heading text-lg font-semibold leading-snug text-slate-900">
                {t("landing.founderQuote")}
              </p>
              <footer className="mt-3 text-sm text-slate-500">— {t("landing.founderName")}, {t("landing.founderLabel")}</footer>
            </blockquote>
            <div className="mt-8 rounded-2xl border border-slate-200 p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("landing.founderLabel")}</p>
              <p className="mt-1 font-heading text-xl font-bold">{t("landing.founderName")}</p>
              <p className="text-sm text-slate-500">{t("landing.founderRole")}</p>
              <div className="mt-4 space-y-2 text-sm text-slate-600">
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-600" /> {t("landing.founderAddress")}</p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-amber-600" />
                  <a href="mailto:support@classroom2corporate.online" className="text-amber-600 hover:underline" data-testid="contact-email">
                    support@classroom2corporate.online
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Teach with us */}
      <TeachWithUs />

      {/* Register interest */}
      <VisitorInterest />

      {/* Featured courses */}
      <FeaturedCourses />

      <FaqSection />

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-amber-600 px-8 py-16 text-center text-white sm:px-16">
          <GraduationCap className="mx-auto h-10 w-10 opacity-80" />
          <h2 className="mt-5 font-heading text-3xl font-extrabold sm:text-4xl">{t("landing.ctaTitle")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-amber-100">
            {t("landing.ctaSubtitle")}
          </p>
          <Button
            onClick={goToApp}
            size="lg"
            className="mt-8 h-12 bg-white px-8 text-base font-semibold text-amber-700 hover:bg-amber-50"
            data-testid="final-cta"
          >
            {user ? t("common.goToDashboard") : t("common.getStartedFree")} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 py-12 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Classroom to Corporate" className="h-8 w-8 object-contain" />
              <span className="font-heading font-bold">Classroom to <span className="text-amber-600">Corporate</span></span>
            </div>
            <p className="mt-3 max-w-xs text-sm text-slate-500">
              {t("landing.footerTagline")}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{t("landing.footerCompany")}</p>
            <p className="mt-3 text-sm text-slate-500">{t("landing.founderRole")}</p>
            <p className="text-sm text-slate-500">{t("landing.founderAddress")}</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{t("landing.footerContact")}</p>
            <a href="mailto:support@classroom2corporate.online" className="mt-3 block text-sm text-amber-600 hover:underline">
              support@classroom2corporate.online
            </a>
          </div>
        </div>
        <div className="border-t border-slate-100">
          <p className="mx-auto max-w-7xl px-4 sm:px-6 py-6 text-sm text-slate-400" data-testid="footer-credit">
            © {new Date().getFullYear()} Classroom to Corporate · Prepared by &amp; all rights reserved to <span className="font-semibold text-slate-500">Darshan Thakkar</span>
          </p>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
}
